import {
  Button,
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Check as CheckIcon,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";

import CustomAudioPlayer from "./AudioPreview";
import aduioGIF from "../../../assets/5404d46b-6c7b-4bae-88b3-6cfd12c07124.gif";

const AudioRecorder = ({ handleChange, audioFile, toggleAudio }) => {
  const [recording, setRecording] = useState(false);
  const [mp3Blob, setMp3Blob] = useState(null);
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);
  const [extension, setExtension] = useState("");
  const isMobile = useMediaQuery("(max-width: 600px)");

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const [audioURL, setAudioURL] = useState("");

  const startCountdown = () => {
    setCountdown(3);
    const countdownInterval = setInterval(() => {
      setCountdown((prevCountdown) => prevCountdown - 1);
    }, 1000);
    setTimeout(() => {
      clearInterval(countdownInterval);
      setCountdown(null);
    }, 3000);
    setTimeout(() => {
      startRecording();
    }, 2000); // Start the recording 200 milliseconds before the countdown ends
  };

  const startRecording = () => {
    console.log("Recording starts");
    audioChunksRef.current = [];
    setTimer(0);
    intervalRef.current = setInterval(() => {
      setTimer((prevTimer) => prevTimer + 1);
    }, 1000);
    var options = {
      audioBitsPerSecond: 256000,
      videoBitsPerSecond: 2500000,
      bitsPerSecond: 2628000,
      mimeType: "audio/" + extension + ";codecs=opus",
    };

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/raw"});
          // convertToWav(audioBlob);
          resampleAudio(audioBlob, 44100);
          audioChunksRef.current = [];
        };

        mediaRecorder.start();
        setRecording(true);
      })
      .catch((error) => {
        console.error("Error starting recording:", error);
      });
  };

  const stopRecording = () => {
    clearInterval(intervalRef.current);
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && recording) {
      mediaRecorder.stop();
      setRecording(false);
    }
  };

  const resampleAudio = async (audioBlob, targetSampleRate) => {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Resample audio to 44.1 kHz
    const offlineContext = new OfflineAudioContext(audioBuffer.numberOfChannels, audioBuffer.duration * targetSampleRate, targetSampleRate);
    const bufferSource = offlineContext.createBufferSource();
    bufferSource.buffer = audioBuffer;
    bufferSource.connect(offlineContext.destination);
    bufferSource.start(0);

    const resampledBuffer = await offlineContext.startRendering();
    const wavBlob = encodeWAV(resampledBuffer);
    setMp3Blob(wavBlob);
    const url = URL.createObjectURL(wavBlob);
    setAudioURL(url);

    // Calculate audio duration
    const duration = resampledBuffer.duration.toFixed(2);
    setTimer(duration);

    // Play the audio automatically after recording stops
    if (audioRef.current) {
      audioRef.current.play();
    }
  };

  // const convertToWav = async (audioBlob) => {
  //   const arrayBuffer = await audioBlob.arrayBuffer();
  //   const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  //   const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  //   const wavBlob = encodeWAV(audioBuffer);
  //   setMp3Blob(wavBlob);
  //   const url = URL.createObjectURL(wavBlob);
  //   setAudioURL(url);
  //   setTimer(audioBuffer.duration.toFixed(2));

  //   // Play the audio automatically after recording stops
  //   if (audioRef.current) {
  //     audioRef.current.play();
  //   }
  // };

  const encodeWAV = (audioBuffer) => {
    const numberOfChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM

    const samples = audioBuffer.getChannelData(0);
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    /* RIFF identifier */
    writeString(view, 0, 'RIFF');
    /* file length */
    view.setUint32(4, 36 + samples.length * 2, true);
    /* RIFF type */
    writeString(view, 8, 'WAVE');
    /* format chunk identifier */
    writeString(view, 12, 'fmt ');
    /* format chunk length */
    view.setUint32(16, 16, true);
    /* sample format (raw) */
    view.setUint16(20, format, true);
    /* channel count */
    view.setUint16(22, numberOfChannels, true);
    /* sample rate */
    view.setUint32(24, sampleRate, true);
    /* byte rate (sample rate * block align) */
    view.setUint32(28, sampleRate * 4, true);
    /* block align (channel count * bytes per sample) */
    view.setUint16(32, numberOfChannels * 2, true);
    /* bits per sample */
    view.setUint16(34, 16, true);
    /* data chunk identifier */
    writeString(view, 36, 'data');
    /* data chunk length */
    view.setUint32(40, samples.length * 2, true);

    floatTo16BitPCM(view, 44, samples);

    return new Blob([view], { type: 'audio/wav' });
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  };

  const handleSave = () => {
    const uniqueFile = new File([mp3Blob], `${Date.now()}.wav`, {
      type: "audio/wav",
    });
    const fileObject = {
      file: uniqueFile,
      metadata: {
        duration: parseFloat(timer),
        type: uniqueFile.type,
        size: uniqueFile.size,
      },
    };
    // here handleChange should recieve: {file: fileObject, metadata: metadataObject}
    handleChange(fileObject);
    setOpen(true);
    if (toggleAudio) {
      toggleAudio();
    }
    setTimeout(() => {
      setOpen(false);
    }, 3000); // Close the dialog after 3 seconds
  };
  const handleDelete = () => {
    setMp3Blob(null);
    setOpen(false);
  };
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };
  useEffect(() => {
    setMp3Blob(audioFile);
  }, [audioFile]);

  useEffect(() => {
    if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
      setExtension("webm");
    } else {
      setExtension("ogg");
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {mp3Blob ? (
        <>
          <CustomAudioPlayer
            audioFile={mp3Blob ? URL.createObjectURL(mp3Blob) : ""}
            duration={timer}
            redoFunction={handleDelete}
            showSave={true}
            showControl={false}
            handleSave={handleSave}
            sliderColor={"primary"}
          />
        </>
      ) : (
        <>
          {countdown ? (
            <Typography variant="h6" style={{ color: "#CB3535" }}>
              {countdown}
            </Typography>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Button
                onClick={recording ? stopRecording : startCountdown}
                startIcon={
                  recording ? (
                    <Stop style={{ color: "white" }} />
                  ) : (
                    <PlayArrow />
                  )
                }
                style={{
                  backgroundColor: recording ? "#CB3535" : "#3576CB",
                  marginTop: "10px",
                  marginBottom: "10px",
                  color: recording ? "white" : "white",
                  padding: "0.5em",
                }}
                color={recording ? "secondary" : "primary"}
              >
                {recording ?
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={aduioGIF}
                      alt="audio gif"
                      style={{
                        maxHeight: "3em",
                        width: "auto",
                        height: "auto",
                      }}
                    />
                    <Typography
                      variant={isMobile ? "body1" : "body2"}
                      color="textSecondary"
                      style={{
                        margin: isMobile ? "4px 0" : "0 4px",
                        textAlign: isMobile ? "center" : "initial",
                        color: "white",
                      }}
                    >
                      {formatTime(timer)}
                    </Typography>
                  </div>
                  : "Start Recording"}
              </Button>
            </div>
          )}
        </>
      )}
      <Dialog open={open} aria-labelledby="dialog-title">
        <DialogContent
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            padding: "16px",
          }}
        >
          <div
            style={{
              margin: "16px",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "green",
              border: "4px solid gray",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CheckIcon style={{ color: "white", fontSize: "28px" }} />
          </div>
          <div style={{ margin: "16px", fontSize: "16px" }}>Audio Saved</div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AudioRecorder;
